import type { PeticePeticeDto } from '@gov-nx/api';
import {
	ePetitionRevokeQuery,
	ePetitionSignQuery,
	usePoMutation,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import type { Nullable } from '@gov-nx/core/types';
import { EPetitionStatus } from '@gov-nx/module/page';
import { usePersonStoreIndividual } from '@gov-nx/store';
import { useServiceAuthorization } from '../Autorizace/useServiceAuthorization';

export const AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT =
	'PODANI_EPETICE_PODEPSAT';
export const AUTHORIZATION_SERVICE_NAME_EPETICE_ODVOLAT =
	'PODANI_EPETICE_ODVOLAT';
export const AUTHORIZATION_SERVICE_NAME_EPETICE_ZALOZIT =
	'PODANI_EPETICE_ZALOZIT';

/** @deprecated Use AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT */
export const AUTHORIZATION_SERVICE_NAME_EPETICE_SIGN =
	AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT;

export interface PetitionSignProps {
	petition: Nullable<PeticePeticeDto>;
	onSignSuccess: (polozkaAutorizaceId?: string) => void;
	onSignError: () => void;
	onRevokeSuccess: (polozkaAutorizaceId?: string) => void;
	onRevokeError: () => void;
}

export const usePetitionSign = ({
	petition,
	onSignSuccess,
	onRevokeSuccess,
	onSignError,
	onRevokeError,
}: PetitionSignProps) => {
	const { isAdult } = usePersonStoreIndividual();
	const { isLowLoa, isDataBoxLoginType } = useIdentity();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT);

	const {
		isLowAuthorization: isLowAuthorizationRevoke,
		isAuthorizationEnforced: isAuthorizationEnforcedRevoke,
	} = useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_ODVOLAT);

	const signQuery = usePoMutation({
		mutationKey: ['e-petition-sign', petition?.id],
		mutationFn: ePetitionSignQuery,
		onSuccess: (response) =>
			onSignSuccess && onSignSuccess(response?.headers?.['x-autorizaceid']),
		onError: () => onSignError && onSignError(),
	});
	const revokeQuery = usePoMutation({
		mutationKey: ['e-petition-revoke', petition?.id],
		mutationFn: ePetitionRevokeQuery,
		onSuccess: (response) =>
			onRevokeSuccess && onRevokeSuccess(response?.headers?.['x-autorizaceid']),
		onError: () => onRevokeError && onRevokeError(),
	});

	const isPublic = petition?.stav === EPetitionStatus.zverejnena;
	const isFounder = petition?.mnouZalozene;
	const isSigned = petition?.mnouPodepsane;

	const canSign =
		isPublic &&
		!isSigned &&
		isAdult &&
		!isLowLoa &&
		!isDataBoxLoginType &&
		!isFounder;
	const canRevoke = isSigned && !isLowLoa && !isDataBoxLoginType && !isFounder;

	return {
		signQuery,
		revokeQuery,
		canRevoke,
		canSign,
		isPublic,
		isFounder,
		isLowAuthorization,
		isAuthorizationEnforced,
		isLowAuthorizationRevoke,
		isAuthorizationEnforcedRevoke,
	};
};
